import { useMemo } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { match, P } from 'ts-pattern';
import { useIxtBuildsV1RepoQueryServiceRepoGet } from '@interxfi/api-client/generated/hooks/ixt-builds-v1-repo-query-service-repo-get';
import { PageHeader } from '@interxfi/app-components/components/layout/page-header/page-header';
import {
  DEFAULT_PAGE_LAYOUT_GAP,
  DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE,
  PageLayout,
} from '@interxfi/app-components/components/layout/page-layout/page-layout';
import { AppReadonlyListStack } from '@interxfi/app-components/components/readonly-list/app-readonly-list-stack';
import { BoxedAppReadonlyList } from '@interxfi/app-components/components/readonly-list/boxed-app-readonly-list';
import { type AggregateBreadcrumb, buildBreadcrumb } from '@interxfi/app-components/components/router-breadcrumbs/router-breadcrumbs';
import { HorizontalStack } from '@interxfi/design-system/components/alignment/horizontal-stack';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { LAYOUT_ZERO_CONTAINER_PADDING } from '@interxfi/design-system/components/layout-container/layout-container';
import {
  DEFAULT_WRAPPING_LAYOUT_COLUMN,
  WrappingLayoutContainer,
} from '@interxfi/design-system/components/layout-container/wrapping/wrapping-layout-container';
import type { ReadonlyListItem } from '@interxfi/design-system/components/list/readonly/item/readonly-list-item';
import type { ReadonlyListGroup } from '@interxfi/design-system/components/list/readonly/stacked/readonly-list-stack';
import { Typography } from '@interxfi/design-system/components/typography';
import type { IxtBuildsV1RepoState } from '@interxfi/types';
import { O5BuildActionsTable } from '../../../../../../../../../features/o5/builds/github/o5-repo-build-actions-table';
import { O5RepoEntityMenu } from '../../../../../../../../../features/o5/builds/github/o5-repo-entity-menu';
import { O5BuildRepoEventSummary } from '../../../../../../../../../features/o5/builds/github/o5-repo-event';
import { getO5RepoStateListItems } from '../../../../../../../../../features/o5/builds/github/o5-repo-list-items';
import { O5RepoStatusPill } from '../../../../../../../../../features/o5/builds/github/o5-repo-status-pill';
import { buildDefaultErrorMessageGetter, useErrorHandler } from '../../../../../../../../../util/error-handler';
import { getPageHead } from '../../../../../../../../../util/routing/meta';

export const Route = createFileRoute('/_app/_o5/_o5-required/tech-ops/build/repo/_repo/$owner/$name')({
  loader: ({ params }) =>
    ({
      breadcrumb: buildBreadcrumb({
        id: `o5-repo-${params.owner}-${params.name}`,
        label: `${params.owner}/${params.name}`,
        to: `/tech-ops/build/repo/${params.owner}/${params.name}`,
      }),
    }) satisfies AggregateBreadcrumb,
  head: ({ params }) => getPageHead(`Repo: ${params.owner}/${params.name}`),
  component: O5BuildRepoDetail,
});

const errorMessageGetter = buildDefaultErrorMessageGetter('Failed to load repo', 'view this repo');

function getListItems(data: IxtBuildsV1RepoState | undefined) {
  const items = getO5RepoStateListItems(data);

  return {
    left: [
      {
        id: 'main',
        items: [items.owner, items.name, items.data.latest, items.data.checksEnabled],
      },
    ] as const satisfies ReadonlyListGroup[],
    right: [items.metadata.createdAt, items.metadata.updatedAt] as const satisfies ReadonlyListItem[],
  } as const;
}

function O5BuildRepoDetail() {
  const { name, owner } = Route.useParams();
  const request = { name, owner } as const;
  const { data, error, isPending } = useIxtBuildsV1RepoQueryServiceRepoGet(request);
  useErrorHandler(error, errorMessageGetter);
  const showPending = Boolean(isPending || error);
  const listItems = useMemo(() => getListItems(data?.repo), [data?.repo]);

  return (
    <PageLayout maxWidthContainer={DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE}>
      <PageLayout.Section noBorder>
        <PageHeader>
          <HorizontalStack gap={2} wrap="nowrap">
            <O5RepoStatusPill isPending={showPending} size="large" status={data?.repo?.status} />
          </HorizontalStack>
          <O5RepoEntityMenu {...request} />
        </PageHeader>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Events</Typography.Level3>

          <O5BuildRepoEventSummary gap={DEFAULT_PAGE_LAYOUT_GAP} padding={LAYOUT_ZERO_CONTAINER_PADDING} request={request} />
        </VerticalStack>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Data</Typography.Level3>

          <WrappingLayoutContainer
            gap={DEFAULT_PAGE_LAYOUT_GAP}
            left={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <AppReadonlyListStack groups={listItems.left} isPending={showPending} />,
            }}
            padding={LAYOUT_ZERO_CONTAINER_PADDING}
            right={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <BoxedAppReadonlyList isPending={showPending} items={listItems.right} />,
            }}
            wrap="forward"
            wrapGap={7}
          />
        </VerticalStack>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={7}>
          <Typography.Level3>Hooks</Typography.Level3>

          {data?.repo?.data?.hooks?.map((hook, i) => {
            const condition = match(hook.condition)
              .with({ branch: P.not(P.nullish) }, (b) => `Condition (Branch): ${b.branch}`)
              .with({ tag: P.not(P.nullish) }, (b) => `Condition (Tag): ${b.tag}`)
              .otherwise(() => `Hook ${i + 1} Condition`);

            return (
              <VerticalStack gap={4} key={condition}>
                <Typography.Level3>{condition}</Typography.Level3>

                <HorizontalStack blockAlign="center" gap={4} wrap="nowrap">
                  <Typography.Level2>Actions</Typography.Level2>
                </HorizontalStack>

                <O5BuildActionsTable actions={hook.actions || []} />
              </VerticalStack>
            );
          })}
        </VerticalStack>
      </PageLayout.Section>
    </PageLayout>
  );
}
